import React, { useCallback, useEffect, useMemo, useState } from "react";
import { toast } from "react-toastify";
import {
  Button,
  Input,
  Label,
  Modal,
  ModalBody,
  ModalFooter,
  ModalHeader,
} from "reactstrap";
import ConfirmActionModal from "components/common/ConfirmActionModal";
import RegenerateInvoiceModal from "components/common/RegenerateInvoiceModal";
import CancelInvoiceModal from "components/common/CancelInvoiceModal";
import {
  approveWorkOrderForBilling,
  cancelWorkOrder,
  closeWorkOrder,
  holdWorkOrder,
  regenerateWorkOrderInvoice,
  resumeWorkOrder,
  startWorkOrder,
  submitWorkOrderBilling,
} from "features/WorkOrders/services/WorkOrderService";
import { cancelInvoice } from "features/Invoices/services/InvoiceService";
import type { WorkOrder } from "features/WorkOrders/types/work-order.types";
import {
  createEmptyAdhocWorkOrderFormRow,
  boqRowToWorkOrderFormRow,
  formWorkOrderItemRowToPersistPayload,
  mapApiWorkOrderItemToFormRow,
  workOrderFormRowToBoqRow,
  type WorkOrderBoqFormRow,
} from "features/WorkOrders/utils/work-order-item.utils";
import { formatCurrency } from "utils/currency";
import LinkedClientAdvancesTable from "components/common/LinkedClientAdvancesTable";
import {
  ClientAdvanceSourceType,
  ClientAdvanceStatus,
} from "features/ClientAdvances/constants/client-advance.constants";
import type { ClientAdvance } from "features/ClientAdvances/types/client-advance.types";

export type WorkOrderLifecycleIntent =
  | { kind: "start" }
  | { kind: "hold" }
  | { kind: "resume" }
  | { kind: "close" }
  | { kind: "approveForBilling" }
  | { kind: "submitBilling" }
  | { kind: "cancel" }
  | { kind: "regenerateInvoice" }
  | { kind: "cancelActiveInvoice"; invoiceId: number };

type ConfirmKind = "start" | "resume" | "close" | "cancel" | "approveForBilling";

const confirmCopy: Record<
  ConfirmKind,
  { title: string; iconClass: string; iconColor: string; heading: string; description: string; confirm: string; confirmColor: string }
> = {
  start: {
    title: "Start work order",
    iconClass: "ri-play-circle-line",
    iconColor: "text-success",
    heading: "Start work on this order?",
    description:
      "Status will move to In progress. The server may record the actual start time if it was not set already.",
    confirm: "Start work",
    confirmColor: "success",
  },
  resume: {
    title: "Resume work",
    iconClass: "ri-play-circle-line",
    iconColor: "text-success",
    heading: "Resume this work order?",
    description:
      "Work will move back to In progress. The server may set the actual start time if it was never set.",
    confirm: "Resume",
    confirmColor: "success",
  },
  close: {
    title: "Close work order",
    iconClass: "ri-lock-line",
    iconColor: "text-dark",
    heading: "Close this work order?",
    description:
      "Marks the job administratively closed after completion. This action is controlled by the server transition rules.",
    confirm: "Close",
    confirmColor: "dark",
  },
  cancel: {
    title: "Cancel work order",
    iconClass: "ri-close-circle-line",
    iconColor: "text-danger",
    heading: "Cancel this work order?",
    description:
      "This will mark the work order as cancelled. This action cannot be undone. " +
      "Any linked advance that hasn't been received yet is cancelled automatically; " +
      "if one has already been received, this will be blocked until it's refunded first.",
    confirm: "Cancel work order",
    confirmColor: "danger",
  },
  approveForBilling: {
    title: "Approve for billing",
    iconClass: "ri-check-double-line",
    iconColor: "text-success",
    heading: "Approve this work order for billing?",
    description:
      "Status will move to Ready for billing. Accounts can then finalise the billed amount and generate the invoice. " +
      "Requires all tasks complete, no open material requests, and at least one active BOQ line item.",
    confirm: "Approve for billing",
    confirmColor: "success",
  },
};

function axiosMessage(err: unknown, fallback: string): string {
  return (
    (err as { response?: { data?: { message?: string } } })?.response?.data
      ?.message ??
    (err as Error)?.message ??
    fallback
  );
}

type Props = {
  workOrder: WorkOrder | null;
  intent: WorkOrderLifecycleIntent | null;
  onFinished: () => void;
  onSuccess: () => void | Promise<void>;
};

export default function WorkOrderLifecycleModals({
  workOrder,
  intent,
  onFinished,
  onSuccess,
}: Props) {
  const [confirmKind, setConfirmKind] = useState<ConfirmKind | null>(null);
  const [confirmSubmitting, setConfirmSubmitting] = useState(false);

  // Hold
  const [holdOpen, setHoldOpen] = useState(false);
  const [holdReason, setHoldReason] = useState("");
  const [holdSubmitting, setHoldSubmitting] = useState(false);

  // Submit billing
  const [billingOpen, setBillingOpen] = useState(false);
  const [billingAmount, setBillingAmount] = useState<string>("");
  const [billingSubmitting, setBillingSubmitting] = useState(false);
  const [draftAdvanceCount, setDraftAdvanceCount] = useState(0);

  // Regenerate invoice
  const [regenerateOpen, setRegenerateOpen] = useState(false);

  // Cancel active invoice
  const [cancelInvoiceOpen, setCancelInvoiceOpen] = useState(false);
  const [cancelInvoiceId, setCancelInvoiceId] = useState<number | null>(null);

  const workOrderItems = workOrder?.items;
  const itemsEditor = useMemo(() => {
    if (workOrderItems == null) return undefined;
    const initialRows: WorkOrderBoqFormRow[] = workOrderItems.length
      ? workOrderItems.map((item) =>
          workOrderFormRowToBoqRow(mapApiWorkOrderItemToFormRow(item)),
        )
      : [workOrderFormRowToBoqRow(createEmptyAdhocWorkOrderFormRow())];
    return {
      initialRows,
      getDefaultRow: () =>
        workOrderFormRowToBoqRow(createEmptyAdhocWorkOrderFormRow()),
      toPayloadItems: (rows: WorkOrderBoqFormRow[]) =>
        rows.map((row) =>
          formWorkOrderItemRowToPersistPayload(boqRowToWorkOrderFormRow(row)),
        ),
    };
  }, [workOrderItems]);

  const runSuccess = useCallback(async () => {
    await onSuccess();
  }, [onSuccess]);

  const closeAll = useCallback(() => {
    setConfirmKind(null);
    setHoldOpen(false);
    setBillingOpen(false);
    setRegenerateOpen(false);
    setCancelInvoiceOpen(false);
  }, []);

  useEffect(() => {
    if (!workOrder || !intent) {
      closeAll();
      return;
    }
    closeAll();
    setHoldReason("");
    setBillingAmount(
      workOrder.finalAmount != null ? String(workOrder.finalAmount) : "",
    );
    setDraftAdvanceCount(0);
    switch (intent.kind) {
      case "start":
        setConfirmKind("start");
        break;
      case "hold":
        setHoldOpen(true);
        break;
      case "resume":
        setConfirmKind("resume");
        break;
      case "close":
        setConfirmKind("close");
        break;
      case "cancel":
        setConfirmKind("cancel");
        break;
      case "approveForBilling":
        setConfirmKind("approveForBilling");
        break;
      case "submitBilling":
        setBillingOpen(true);
        break;
      case "regenerateInvoice":
        setRegenerateOpen(true);
        break;
      case "cancelActiveInvoice":
        setCancelInvoiceId(intent.invoiceId);
        setCancelInvoiceOpen(true);
        break;
      default:
        break;
    }
  }, [workOrder, intent, closeAll]);

  // --- Simple confirmations (start, resume, close, cancel, approveForBilling) ---
  const handleConfirmLifecycle = useCallback(async () => {
    if (!workOrder || !confirmKind) return;
    setConfirmSubmitting(true);
    try {
      let res;
      if (confirmKind === "start") res = await startWorkOrder(workOrder.id);
      else if (confirmKind === "resume") res = await resumeWorkOrder(workOrder.id);
      else if (confirmKind === "cancel") res = await cancelWorkOrder(workOrder.id);
      else if (confirmKind === "approveForBilling") res = await approveWorkOrderForBilling(workOrder.id);
      else res = await closeWorkOrder(workOrder.id);

      if (res.success) {
        toast.success(res.message ?? "Updated.");
        setConfirmKind(null);
        await runSuccess();
        onFinished();
      } else {
        toast.error(res.message ?? "Request failed.");
      }
    } catch (err: unknown) {
      toast.error(axiosMessage(err, "Request failed."));
    } finally {
      setConfirmSubmitting(false);
    }
  }, [confirmKind, onFinished, runSuccess, workOrder]);

  // --- Hold ---
  const handleHold = useCallback(async () => {
    if (!workOrder || !holdReason.trim()) return;
    setHoldSubmitting(true);
    try {
      const res = await holdWorkOrder(workOrder.id, { reason: holdReason.trim() });
      if (res.success) {
        toast.success(res.message ?? "Work order put on hold.");
        setHoldOpen(false);
        await runSuccess();
        onFinished();
      } else {
        toast.error(res.message ?? "Could not put work order on hold.");
      }
    } catch (err: unknown) {
      toast.error(axiosMessage(err, "Could not put work order on hold."));
    } finally {
      setHoldSubmitting(false);
    }
  }, [holdReason, onFinished, runSuccess, workOrder]);

  // --- Submit billing ---
  const handleSubmitBilling = useCallback(async () => {
    if (!workOrder) return;
    const parsed = billingAmount.trim() !== "" ? Number(billingAmount) : null;
    if (billingAmount.trim() !== "" && (isNaN(parsed!) || parsed! < 0)) {
      toast.error("Please enter a valid billed amount.");
      return;
    }
    setBillingSubmitting(true);
    try {
      const res = await submitWorkOrderBilling(workOrder.id, {
        finalAmount: parsed,
      });
      if (res.success) {
        toast.success(res.message ?? "Billing submitted. Invoice generated.");
        setBillingOpen(false);
        await runSuccess();
        onFinished();
      } else {
        toast.error(res.message ?? "Could not submit billing.");
      }
    } catch (err: unknown) {
      toast.error(axiosMessage(err, "Could not submit billing."));
    } finally {
      setBillingSubmitting(false);
    }
  }, [billingAmount, onFinished, runSuccess, workOrder]);

  const confirmCopyEntry = confirmKind ? confirmCopy[confirmKind] : null;

  return (
    <>
      {/* Simple confirmations */}
      {confirmCopyEntry ? (
        <ConfirmActionModal
          show={Boolean(confirmKind)}
          onClose={() => {
            if (!confirmSubmitting) {
              setConfirmKind(null);
              onFinished();
            }
          }}
          title={confirmCopyEntry.title}
          iconClass={confirmCopyEntry.iconClass}
          iconColor={confirmCopyEntry.iconColor}
          heading={confirmCopyEntry.heading}
          description={confirmCopyEntry.description}
          confirmText={confirmSubmitting ? "Please wait…" : confirmCopyEntry.confirm}
          confirmColor={confirmCopyEntry.confirmColor}
          confirmDisabled={confirmSubmitting}
          onConfirm={() => void handleConfirmLifecycle()}
        />
      ) : null}

      {/* Hold — collects required reason */}
      <Modal
        isOpen={holdOpen}
        toggle={() => { if (!holdSubmitting) { setHoldOpen(false); onFinished(); } }}
        centered
      >
        <ModalHeader toggle={() => { if (!holdSubmitting) { setHoldOpen(false); onFinished(); } }}>
          Put work on hold
        </ModalHeader>
        <ModalBody>
          <p className="text-muted mb-3">
            Work will move to <strong>On hold</strong> until someone resumes it.
          </p>
          <Label className="form-label">Reason <span className="text-danger">*</span></Label>
          <Input
            type="textarea"
            rows={3}
            placeholder="Explain why the work is being paused…"
            value={holdReason}
            onChange={(e) => setHoldReason(e.target.value)}
            disabled={holdSubmitting}
          />
        </ModalBody>
        <ModalFooter>
          <Button color="light" disabled={holdSubmitting} onClick={() => { setHoldOpen(false); onFinished(); }}>
            Cancel
          </Button>
          <Button color="secondary" disabled={holdSubmitting || !holdReason.trim()} onClick={() => void handleHold()}>
            {holdSubmitting ? "Please wait…" : "Put on hold"}
          </Button>
        </ModalFooter>
      </Modal>

      {/* Submit billing — optional finalAmount */}
      <Modal
        isOpen={billingOpen}
        toggle={() => { if (!billingSubmitting) { setBillingOpen(false); onFinished(); } }}
        centered
      >
        <ModalHeader toggle={() => { if (!billingSubmitting) { setBillingOpen(false); onFinished(); } }}>
          Submit billing
        </ModalHeader>
        <ModalBody>
          <p className="text-muted mb-3">
            This will finalise the billed amount, generate the invoice, and close all work assignments.
            Status will move to <strong>Completed</strong>.
          </p>
          {workOrder ? (
            <p className="text-muted small mb-3">
              Current agreed amount:{" "}
              <span className="text-body fw-medium">
                {workOrder.agreedAmount != null
                  ? formatCurrency(workOrder.agreedAmount)
                  : "—"}
              </span>
            </p>
          ) : null}
          <Label className="form-label">
            Final billed amount <span className="text-muted fw-normal">(optional — leave blank to use agreed amount)</span>
          </Label>
          <Input
            type="number"
            min={0}
            step="0.01"
            placeholder="e.g. 15000"
            value={billingAmount}
            onChange={(e) => setBillingAmount(e.target.value)}
            disabled={billingSubmitting}
          />
          {draftAdvanceCount > 0 ? (
            <div className="alert alert-warning py-2 px-3 mt-3 mb-0 small">
              Cannot proceed: {draftAdvanceCount} advance
              {draftAdvanceCount !== 1 ? "s" : ""} linked to this record{" "}
              {draftAdvanceCount !== 1 ? "are" : "is"} still awaiting receipt.
              All advances must be received or cancelled first.
            </div>
          ) : null}
          {workOrder ? (
            <LinkedClientAdvancesTable
              className="mt-3"
              sourceType={ClientAdvanceSourceType.WORK_ORDER}
              sourceId={workOrder.id}
              onLoaded={(advances: ClientAdvance[]) =>
                setDraftAdvanceCount(
                  advances.filter((a) => a.status === ClientAdvanceStatus.DRAFT)
                    .length,
                )
              }
            />
          ) : null}
        </ModalBody>
        <ModalFooter>
          <Button color="light" disabled={billingSubmitting} onClick={() => { setBillingOpen(false); onFinished(); }}>
            Cancel
          </Button>
          <Button
            color="primary"
            disabled={billingSubmitting || draftAdvanceCount > 0}
            onClick={() => void handleSubmitBilling()}
          >
            {billingSubmitting ? "Submitting…" : "Submit billing"}
          </Button>
        </ModalFooter>
      </Modal>

      {/* Regenerate invoice — only runs when no active invoice exists */}
      <RegenerateInvoiceModal
        show={regenerateOpen}
        onClose={() => {
          setRegenerateOpen(false);
          onFinished();
        }}
        onSuccess={runSuccess}
        currentFinalAmount={workOrder?.finalAmount}
        itemsEditor={itemsEditor}
        submit={(payload) => {
          if (!workOrder) throw new Error("No work order loaded.");
          return regenerateWorkOrderInvoice(workOrder.id, payload);
        }}
      />

      {/* Cancel active invoice — the only step that ever cancels one */}
      <CancelInvoiceModal
        show={cancelInvoiceOpen}
        onClose={() => {
          setCancelInvoiceOpen(false);
          onFinished();
        }}
        onSuccess={runSuccess}
        submit={(payload) => {
          if (!cancelInvoiceId) throw new Error("No invoice selected.");
          return cancelInvoice(cancelInvoiceId, payload);
        }}
      />
    </>
  );
}
